const EPSILON = 1e-6

export const snapNumber = (value, step) => Math.round(value / step) * step

export function normalizeRotation(rotationDeg, stepDeg) {
  const snapped = snapNumber(rotationDeg, stepDeg)
  return ((snapped % 360) + 360) % 360
}

export function rotatePointY(point, rotationDeg) {
  const radians = (rotationDeg * Math.PI) / 180
  const cosine = Math.cos(radians)
  const sine = Math.sin(radians)
  return [point[0] * cosine + point[2] * sine, point[1], -point[0] * sine + point[2] * cosine]
}

export function snapTransform(position, rotationDeg, grid) {
  return {
    positionM: [
      snapNumber(position[0], grid.horizontalStepM),
      snapNumber(position[1], grid.verticalStepM),
      snapNumber(position[2], grid.horizontalStepM),
    ],
    rotationDeg: [0, normalizeRotation(rotationDeg, grid.rotationStepDeg), 0],
    scale: [1, 1, 1],
  }
}

function dimensionsFor(module, rotationDeg) {
  const dimensions = module.collider?.sizeM || module.dimensionsM
  const quarterTurns = Math.round(normalizeRotation(rotationDeg, 90) / 90)
  return quarterTurns % 2 ? [dimensions[2], dimensions[1], dimensions[0]] : [...dimensions]
}

export function placementAabb(module, transform) {
  const [width, height, depth] = dimensionsFor(module, transform.rotationDeg[1])
  const offset = rotatePointY(module.boundsOffsetM || [0, 0, 0], transform.rotationDeg[1])
  const [x, y, z] = transform.positionM.map((value, index) => value + offset[index])
  return {
    min: [x - width / 2, y, z - depth / 2],
    max: [x + width / 2, y + height, z + depth / 2],
    size: [width, height, depth],
  }
}

export function aabbIntersects(left, right) {
  return [0, 1, 2].every(axis => left.min[axis] < right.max[axis] - EPSILON && left.max[axis] > right.min[axis] + EPSILON)
}

function insidePlot(aabb, plot) {
  return [0, 1, 2].every(axis => aabb.min[axis] >= plot.min[axis] - EPSILON && aabb.max[axis] <= plot.max[axis] + EPSILON)
}

export function snapTypesCompatible(left, right, snapTypes) {
  return Boolean(snapTypes[left]?.includes(right) || snapTypes[right]?.includes(left))
}

function worldSnapPoints(placement, module) {
  return (module.snapPoints || []).map(point => {
    const rotated = rotatePointY(point.positionM, placement.transform.rotationDeg[1])
    return {
      ...point,
      placementId: placement.id,
      positionM: placement.transform.positionM.map((value, index) => value + rotated[index]),
    }
  })
}

function distance(left, right) {
  return Math.hypot(left[0] - right[0], left[1] - right[1], left[2] - right[2])
}

export function findBestSnap({ module, transform, placements, moduleLookup, snapTypes, radiusM }) {
  let best = null
  for (const ownPoint of module.snapPoints || []) {
    const ownOffset = rotatePointY(ownPoint.positionM, transform.rotationDeg[1])
    for (const placement of placements) {
      const targetModule = placement.module || moduleLookup?.(placement.moduleId) || placement.preview
      if (!targetModule) continue
      for (const targetPoint of worldSnapPoints(placement, targetModule)) {
        if (!snapTypesCompatible(ownPoint.type, targetPoint.type, snapTypes)) continue
        const positionM = targetPoint.positionM.map((value, index) => value - ownOffset[index])
        const score = distance(positionM, transform.positionM)
        if (score <= radiusM && (!best || score < best.score)) {
          best = {
            score,
            positionM,
            ownPointId: ownPoint.id,
            targetPointId: targetPoint.id,
            targetPlacementId: placement.id,
          }
        }
      }
    }
  }
  return best
}

export function solvePlacement({
  module,
  cursor,
  rotationDeg = 0,
  grid,
  plot,
  placements = [],
  moduleLookup,
  snapTypes = {},
  supportAvailable = false,
}) {
  let transform = snapTransform(cursor, rotationDeg, grid)
  const snap = findBestSnap({
    module,
    transform,
    placements,
    moduleLookup,
    snapTypes,
    radiusM: Math.max(grid.snapToleranceM, grid.horizontalStepM * 1.25, grid.verticalStepM * 2),
  })
  if (snap) transform = { ...transform, positionM: snap.positionM }

  const aabb = placementAabb(module, transform)
  const reasons = []
  if (module.placement?.requiresPlot && plot && !insidePlot(aabb, plot)) reasons.push('OUTSIDE_PLOT')
  const collision = placements.find(placement => {
    const placedModule = placement.module || moduleLookup?.(placement.moduleId) || placement.preview
    return placedModule && aabbIntersects(aabb, placementAabb(placedModule, placement.transform))
  })
  if (collision) reasons.push('COLLISION')
  const groundAnchor = module.structural?.supportMode === 'ground-anchor'
  if (module.placement?.requiresSupport && !groundAnchor && !supportAvailable && !snap) reasons.push('NO_SUPPORT')

  return {
    valid: reasons.length === 0,
    color: reasons.length ? 'red' : 'green',
    reasons,
    collisionId: collision?.id || null,
    snap,
    transform,
    aabb,
  }
}
